import { instrumenter } from 'client/utils/instrumentation'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'
import { imageAppliedSelector, textAppliedSelector } from '~/client/store/designPersonalization/selectors'

const debounceImagesLoaded = (timeout = 2000): ((a: boolean, b: boolean) => void) => {
  let timer: NodeJS.Timeout | undefined

  return (textPersonalized: boolean, imagePersonalized: boolean): void => {
    if (!timer) {
      timer = setTimeout(() => {
        timer = undefined
        instrumenter.recordImagesLoaded(textPersonalized, imagePersonalized)
      }, timeout)
    }
  }
}

const debouncedRecordImagesLoaded = debounceImagesLoaded()

// perfObserver only works client side
// Use in a useEffect or after checking if window is defined
const perfObserver =
  (textPersonalized: boolean, imagePersonalized: boolean) =>
  (list: PerformanceObserverEntryList): void => {
    list.getEntries().forEach((entry) => {
      if (entry.name.includes('rendering')) {
        if (!window.imageResources) {
          window.imageResources = []
        }
        // each image gets an entry and its load time is processed in instrumenter.recordImagesLoaded
        window.imageResources.push(entry as PerformanceResourceTiming)
        debouncedRecordImagesLoaded(textPersonalized, imagePersonalized)
      }
    })
  }

export const ImageLoadedInstrumenter = (): null => {
  const imagePersonalized = useSelector(imageAppliedSelector)
  const textPersonalized = useSelector(textAppliedSelector)

  useEffect(() => {
    if (!window.imageResources) {
      window.imageResources = []
    }

    window.windowObserver?.disconnect()
    window.windowObserver = new PerformanceObserver(perfObserver(textPersonalized, imagePersonalized))
    // observe network resouce loading (specifically rendering urls)
    window.windowObserver.observe({ type: 'resource' })
  }, [textPersonalized, imagePersonalized])

  useEffect(() => {
    if (!window.imageResources) {
      window.imageResources = []
    }

    // run this once when the page loads, assuming we have loaded all of the initial images
    instrumenter.recordImagesLoaded(textPersonalized, imagePersonalized)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
